import React, { useState, useEffect } from "react";
import { opcionesAPI, type Sucursal } from "@/lib/api";
import { toast } from "sonner";
import { ListNewLoading, ListToolbar } from "@/components/ListNewButton";

interface Props {
  onEdit?: (item: Sucursal) => void;
  onNew?: () => void;
}

export function SucursalesListado({ onEdit, onNew }: Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await opcionesAPI.listSucursales();
        setSucursales(data);
      } catch (error) {
        console.error("Error loading sucursales:", error);
        toast.error("Error al cargar sucursales");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredSucursales = sucursales.filter(s =>
    !searchQuery ||
    s.codigo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.direccion?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.ciudad?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.provincia?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.telefono?.includes(searchQuery) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <ListNewLoading onNew={onNew} message="Cargando sucursales..." />;
  }

  return (
    <div className="space-y-4 mt-4">
      <ListToolbar onNew={onNew}>
        <label className="block text-sm font-medium mb-1">Buscar</label>
        <input
          type="text"
          placeholder="Buscar por cualquier campo..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
        />
      </ListToolbar>

      {filteredSucursales.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          No se encontraron sucursales
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2 text-left">Código</th>
                <th className="px-4 py-2 text-left">Nombre</th>
                <th className="px-4 py-2 text-left">Dirección</th>
                <th className="px-4 py-2 text-left">Ciudad</th>
                <th className="px-4 py-2 text-left">Provincia</th>
                <th className="px-4 py-2 text-left">Teléfono</th>
                <th className="px-4 py-2 text-left">Email</th>
              </tr>
            </thead>
            <tbody>
              {filteredSucursales.map((sucursal) => (
                <tr
                  key={sucursal.codigo}
                  className="border-b hover:bg-muted/50 cursor-pointer"
                  onClick={() => onEdit?.(sucursal)}
                >
                  <td className="px-4 py-2">{sucursal.codigo}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-2">
                      {sucursal.nombre}
                      {(sucursal.esCasaCentral || sucursal.es_casa_central) ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                          Casa central
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-4 py-2">{sucursal.direccion || '-'}</td>
                  <td className="px-4 py-2">{sucursal.ciudad || '-'}</td>
                  <td className="px-4 py-2">{sucursal.provincia || '-'}</td>
                  <td className="px-4 py-2">{sucursal.telefono || '-'}</td>
                  <td className="px-4 py-2">{sucursal.email || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}